// Angular
import { Observable, takeUntil } from 'rxjs';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';

// Libraries

import { TranslateService } from '@ngx-translate/core';

import { Select, Store } from '@ngxs/store';

// Models
import { AppCleanup } from '../../../../models';
import { SyncService, SyncStatusType } from '../../../../services';
import { ConfirmDialogModel } from '../../../../dialogs/confirm-dialog/confirm-dialog.component';

// Services
import { DCGService } from '../../../../services';
import { CoreService } from '../../../../services';
import { DialogService } from '../../../../services/dialog.service';
import { DeviceService, DeviceUnitModel } from '../../../../../modules/unit-config/services/device.service';

// Enums
import { StatusType } from '../../../../enums';

// Helpers
import { UtilityHelper } from '../../../../helpers';

// Store
import { DeviceState } from '../../../../../modules/asset/device/state/device.state';
import { DeviceActions } from '../../../../../modules/asset/device/state/device.action';

import { SpinnerSizeType } from '../../../spinner/spinner.component';
@Component({
	selector: 'connection-status-dialog',
	templateUrl: './connection-status-dialog.component.html',
	styleUrls: ['./connection-status-dialog.component.scss'],
})
export class ConnectionStatusDialogComponent extends AppCleanup implements OnInit, OnDestroy {
	readonly SpinnerSizeType = SpinnerSizeType;
	@Select(DeviceState.isLoaded) isLoaded$: Observable<boolean>;
	@Select(DeviceState.isHostGateway) isHostGateway$: Observable<boolean>;
	isHostGateway: boolean = null;
	componentTitle: string = 'Connection status';
	statusData: StatusDataViewModel;
	showSpinner: boolean = true;
	callFailed: boolean = false;
	allUnitsSpinner: boolean;
	allUnitsStatus: SyncStatusType;
	unitSpinners: boolean[];
	unitStatuses: SyncStatusType[];
	latestSyncDate: Date;
	latestSyncSpinner: boolean;
	latestSyncStatus: SyncStatusType;
	fullSyncDate: Date;
	fullSyncSpinner: boolean;
	fullSyncStatus: SyncStatusType;
	hideTooltip: boolean = true;
	showMessagesSent: boolean = false;

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.hideTooltip = window.innerWidth > 767;
	}

	constructor(
		private dcgService: DCGService,
		private coreService: CoreService,
		private dialogService: DialogService,
		private deviceService: DeviceService,
		private translateService: TranslateService,
		private syncService: SyncService,
		private store: Store
	) {
		super();
	}

	ngOnInit() {
		this.unitSpinners = [];
		this.unitStatuses = [];
		this.showMessagesSent = !this.isEdge;

		this.syncService.latestSyncDate$.pipe(takeUntil(this.destroy)).subscribe((latestSyncDate) => {
			this.latestSyncDate = latestSyncDate;
		});

		this.syncService.latestSyncSpinner$.pipe(takeUntil(this.destroy)).subscribe((latestSyncSpinner) => {
			this.latestSyncSpinner = latestSyncSpinner;
		});

		this.syncService.latestSyncStatus$.pipe(takeUntil(this.destroy)).subscribe((latestSyncStatus) => {
			this.latestSyncStatus = latestSyncStatus;
		});

		this.syncService.fullSyncDate$.pipe(takeUntil(this.destroy)).subscribe((fullSyncDate) => {
			this.fullSyncDate = fullSyncDate;
		});

		this.syncService.fullSyncSpinner$.pipe(takeUntil(this.destroy)).subscribe((fullSyncSpinner) => {
			this.fullSyncSpinner = fullSyncSpinner;
		});

		this.syncService.fullSyncStatus$.pipe(takeUntil(this.destroy)).subscribe((fullSyncStatus) => {
			this.fullSyncStatus = fullSyncStatus;
		});

		this.isLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			// Only getting device information if device is Edge.
			if (!isLoaded && this.isEdge) {
				this.store.dispatch(new DeviceActions.GetAll({ showSpinner: false, showToast: true }));
			} else {
				this.isHostGateway$.pipe(takeUntil(this.destroy)).subscribe((isHostGateway) => {
					this.isHostGateway = isHostGateway;

					// Only initiate DCG status connection if device is Legacy or Single.
					const isSingleOrLegacy: boolean = !this.isEdge || (this.isEdge && !this.isHostGateway);
					if (isSingleOrLegacy) {
						this.initializeDcgWatchConnectionStatus();
					} else {
						this.showSpinner = false;
						this.callFailed = false;
					}
				});
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	private initializeDcgWatchConnectionStatus(): void {
		this.dcgService
			.getConnectionStatus()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (data) => {
					this.statusData = data;
					this.statusData.units.forEach(() => this.unitSpinners.push(false), this.unitStatuses.push(null));
					this.showSpinner = false;
					this.callFailed = false;
				},
				error: () => {
					this.showSpinner = false;
					this.callFailed = true;
				},
			});
	}

	restartAllUnits() {
		const confirmModel: ConfirmDialogModel = {
			title: 'Restart all units',
			message: 'Are you sure you want to restart all units? By doing this, you might lose data',
			action: this.translateService.instant('ACTION.RESTART_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					let message: string = '';
					this.allUnitsSpinner = true;
					this.dcgService
						.restartAllUnits()
						.pipe(takeUntil(this.destroy))
						.subscribe({
							next: (data) => {
								this.allUnitsSpinner = false;
								message = `${this.translateService.instant('GENERIC.RESTARTED', { name: this.translateService.instant('MODULE.UNIT.TITLE_CAPITAL_PLURAL') })}`;
								this.coreService.toast({ message: message, type: StatusType.SUCCESS });
								this.allUnitsStatus = SyncStatusType.SUCCESS;
							},
							error: () => {
								this.allUnitsSpinner = false;
								message = `${this.translateService.instant('GENERIC.NOT_RESTARTED', { name: this.translateService.instant('MODULE.UNIT.TITLE_TITLE_CAPITAL_PLURAL') })}`;
								this.coreService.toast({ message: message, type: StatusType.DANGER });
								this.allUnitsStatus = SyncStatusType.FAILURE;
							},
						});
				}
			});
	}

	restartUnit(unitGuid: string, index: number) {
		const confirmModel: ConfirmDialogModel = {
			title: `Restart ${this.translateService.instant('MODULE.UNIT.TITLE')}`,
			message: `${this.translateService.instant('MODULE.UNIT.CONFIRM_RESTART')} ${UtilityHelper.capitalizeFirstLetter(this.translateService.instant('GENERIC.WARNING_DATA_LOSS'))}`,
			action: this.translateService.instant('ACTION.RESTART_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.unitSpinners[index] = true;
					let message: string = '';
					this.dcgService.restartUnit(unitGuid).subscribe({
						next: (data) => {
							this.unitSpinners[index] = false;
							this.unitStatuses[index] = SyncStatusType.SUCCESS;
							message = `${this.translateService.instant('GENERIC.RESTARTED', { name: this.translateService.instant('MODULE.UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
							this.enableUnit(unitGuid);
						},
						error: () => {
							this.unitSpinners[index] = false;
							this.unitStatuses[index] = SyncStatusType.FAILURE;
							message = `${this.translateService.instant('GENERIC.NOT_RESTARTED', { name: this.translateService.instant('MODULE.UNIT.TITLE_CAPITAL') })}`;
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						},
					});
				}
			});
	}

	enableUnit(unitGuid: string) {
		let deviceUnit: DeviceUnitModel = new DeviceUnitModel();
		deviceUnit.deviceId = 1;
		deviceUnit.unitGuid = unitGuid;
		deviceUnit.enabled = true;
		this.deviceService.updateDeviceUnit(1, unitGuid, deviceUnit).subscribe({
			next: (data) => {},
			error: () => {},
		});
	}

	sync(syncType: 'all' | 'latest' = 'all') {
		if (this.syncService.isConnected) {
			const confirmModel: ConfirmDialogModel = {
				title: `${syncType === 'all' ? 'Full sync' : 'Delta sync'}`,
				message: `${this.translateService.instant('GENERIC.CONFIRM_SYNC_PROCESS')}`,
				action: 'Sync',
			};

			const dialogRef = this.dialogService.confirmAction(confirmModel);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.destroy))
				.subscribe((result) => {
					if (result) {
						this.syncService.sync(syncType);
					}
				});
		}
	}
}

export class StatusDataViewModel {
	iotHubMessagesSent: MessageViewModel;
	messagesReceived: MessageViewModel;
	networkAvailable: boolean;
	units: UnitStatusViewModel[];
}

export class MessageViewModel {
	count: number;
	size: number;
}

export class UnitStatusViewModel {
	status: string;
	unit: UnitViewModel;
}

export class UnitViewModel {
	unitGuid: string;
	name: string;
	description: string;
}
